import React, { useState, useEffect, useCallback } from 'react';
import {
  FaTrash, FaSearch, FaFilter, FaDesktop, FaMobileAlt, FaTabletAlt,
  FaCheckCircle, FaExclamationCircle, FaUserSecret, FaHistory,
  FaUser, FaUserShield, FaUserTie, FaSignInAlt, FaSignOutAlt,
} from 'react-icons/fa';
import SkeletonLoader from '../../components/ui/SkeletonLoader';
import PageLayout from '../../components/ui/PageLayout';
import PageHeader from '../../components/ui/PageHeader';
import StatCard, { StatGrid } from '../../components/ui/StatCard';
import FilterCard from '../../components/ui/FilterCard';
import DataTable, { TableEmpty } from '../../components/ui/DataTable';
import { useToast } from '../../context/ToastContext';

const TABS = [
  { id: 'user', label: 'User Sessions', icon: FaUser },
  { id: 'admin', label: 'Admin Sessions', icon: FaUserShield },
  { id: 'impersonation', label: 'Impersonation Sessions', icon: FaUserSecret },
  { id: 'employee', label: 'Employee Sessions', icon: FaUserTie },
];

const TAB_META = {
  user: {
    title: 'User Login & Logout History',
    description: 'Client portal sign-in and sign-out activity',
    nameLabel: 'User',
    emptyTitle: 'No User Session Records',
    emptyDescription: 'No user session records match your search criteria.',
  },
  admin: {
    title: 'Admin Login & Logout History',
    description: 'Admin Dashboard sign-in and sign-out activity',
    nameLabel: 'Admin ID',
    emptyTitle: 'No Admin Session Records',
    emptyDescription: 'No admin session records match your search criteria.',
  },
  impersonation: {
    title: 'Admin to User Impersonation Sessions',
    description: 'Impersonation start and end events with session duration',
    nameLabel: 'User',
    emptyTitle: 'No Impersonation Records',
    emptyDescription: 'No impersonation session records match your search criteria.',
  },
  employee: {
    title: 'Employee Login & Logout History',
    description: 'Employee portal sign-in and sign-out activity',
    nameLabel: 'Employee',
    emptyTitle: 'No Employee Session Records',
    emptyDescription: 'No employee session records match your search criteria.',
  },
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

const TH = 'px-3 py-2 text-left text-xs font-semibold whitespace-nowrap';
const TD = 'px-3 py-2 align-middle whitespace-nowrap';
const TD_WRAP = 'px-3 py-2 align-middle';

const formatDuration = (seconds) => {
  if (seconds === null || seconds === undefined) return '—';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (mins > 0) return `${mins}m ${secs}s`;
  return `${secs}s`;
};

const LoginHistory = () => {
  const [activeTab, setActiveTab] = useState('user');
  const [history, setHistory] = useState([]);
  const [counts, setCounts] = useState({ user: 0, admin: 0, impersonation: 0, employee: 0, total: 0 });
  const [eventCounts, setEventCounts] = useState({ login: 0, logout: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [eventFilter, setEventFilter] = useState('all');
  const [selectedIds, setSelectedIds] = useState([]);
  const toast = useToast();

  const API = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';
  const meta = TAB_META[activeTab];

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('adminToken');
      const params = new URLSearchParams({ type: activeTab });
      if (eventFilter !== 'all') params.set('eventType', eventFilter);

      const res = await fetch(`${API}/api/auth/login-history?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to load session history');
      const data = await res.json();
      setHistory(data.history || []);
      if (data.counts) setCounts(data.counts);
      if (data.eventCounts) setEventCounts(data.eventCounts);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [API, activeTab, eventFilter]);

  useEffect(() => {
    setSelectedIds([]);
    setSearch('');
    setStatusFilter('all');
    fetchHistory();
  }, [fetchHistory]);

  const handleTabChange = (tabId) => {
    setEventFilter('all');
    setActiveTab(tabId);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Move this session record to Recycle Bin?')) return;
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API}/api/auth/login-history/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Delete failed');
      toast.success('Record moved to Recycle Bin');
      setHistory((prev) => prev.filter((h) => h._id !== id));
      setCounts((prev) => ({
        ...prev,
        [activeTab]: Math.max(0, (prev[activeTab] || 0) - 1),
        total: Math.max(0, (prev.total || 0) - 1),
      }));
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Move ${selectedIds.length} records to Recycle Bin?`)) return;
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API}/api/auth/login-history/bulk-delete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ ids: selectedIds }),
      });
      if (!res.ok) throw new Error('Bulk delete failed');
      toast.success(`${selectedIds.length} records moved to Recycle Bin`);
      setSelectedIds([]);
      fetchHistory();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const filtered = history.filter((item) => {
    const searchValue = search.toLowerCase();
    const matchesSearch =
      item.userName?.toLowerCase().includes(searchValue) ||
      item.userEmail?.toLowerCase().includes(searchValue) ||
      item.adminLabel?.toLowerCase().includes(searchValue) ||
      item.ip?.toLowerCase().includes(searchValue) ||
      item.os?.toLowerCase().includes(searchValue) ||
      item.browser?.toLowerCase().includes(searchValue) ||
      (item.eventType || 'login').toLowerCase().includes(searchValue);
    const matchesStatus = statusFilter === 'all' || item.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getDeviceIcon = (device) => {
    if (device === 'Mobile') return <FaMobileAlt className="w-3 h-3 text-gray-400 shrink-0" />;
    if (device === 'Tablet') return <FaTabletAlt className="w-3 h-3 text-gray-400 shrink-0" />;
    return <FaDesktop className="w-3 h-3 text-gray-400 shrink-0" />;
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) setSelectedIds(filtered.map((i) => i._id));
    else setSelectedIds([]);
  };

  const handleSelectOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
    );
  };

  if (loading) {
    return <SkeletonLoader variant="page" />;
  }

  const tabCount = counts[activeTab] ?? history.length;
  const loginCount = eventCounts.login ?? history.filter((h) => (h.eventType || 'login') === 'login').length;
  const logoutCount = eventCounts.logout ?? history.filter((h) => h.eventType === 'logout').length;
  const showAdminColumn = activeTab === 'impersonation';
  const colSpan = showAdminColumn ? 10 : 9;

  return (
    <PageLayout>
      <PageHeader
        title="Session History"
        description="Login and logout tracking for users, admins, impersonation, and employees"
        icon={FaHistory}
        badge={
          <span className="inline-flex items-center px-4 py-2 rounded-xl bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 text-sm font-medium border border-primary-100 dark:border-primary-800">
            {counts.total || 0} Total Records
          </span>
        }
      />

      <div className="flex flex-wrap gap-2 mb-6">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => handleTabChange(tab.id)}
              className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all border ${
                isActive
                  ? 'btn-premium !py-2.5 border-transparent'
                  : 'bg-white dark:bg-dark-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-dark-600 hover:bg-gray-50 dark:hover:bg-dark-700'
              }`}
            >
              <Icon className="w-4 h-4" />
              <span>{tab.label}</span>
              <span className={`px-2 py-0.5 rounded-lg text-xs ${
                isActive
                  ? 'bg-white/20 text-white'
                  : 'bg-gray-100 dark:bg-dark-700 text-gray-500 dark:text-gray-400'
              }`}>
                {counts[tab.id] ?? 0}
              </span>
            </button>
          );
        })}
      </div>

      <div className="mb-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{meta.title}</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{meta.description}</p>
      </div>

      <StatGrid>
        <StatCard label="Total in Tab" value={tabCount} icon={TABS.find((t) => t.id === activeTab)?.icon || FaHistory} />
        <StatCard label="Logins" value={loginCount} icon={FaSignInAlt} iconBg="bg-blue-100 dark:bg-blue-900/30" iconColor="text-blue-600 dark:text-blue-400" valueColor="text-blue-600" />
        <StatCard label="Logouts" value={logoutCount} icon={FaSignOutAlt} iconBg="bg-amber-100 dark:bg-amber-900/30" iconColor="text-amber-600 dark:text-amber-400" valueColor="text-amber-600" />
        <StatCard label="Filtered Results" value={filtered.length} icon={FaFilter} iconBg="bg-purple-100 dark:bg-purple-900/30" iconColor="text-purple-600 dark:text-purple-400" />
      </StatGrid>

      <FilterCard>
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search by name, email, admin, IP, OS or browser..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input-modern pl-11 py-2.5 text-sm"
            />
          </div>
          <div className="relative min-w-[180px]">
            <FaFilter className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
            <select
              value={eventFilter}
              onChange={(e) => setEventFilter(e.target.value)}
              className="input-modern pl-11 py-2.5 text-sm cursor-pointer"
            >
              <option value="all">All Events</option>
              <option value="login">Login Only</option>
              <option value="logout">Logout Only</option>
            </select>
          </div>
          <div className="relative min-w-[180px]">
            <FaFilter className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="input-modern pl-11 py-2.5 text-sm cursor-pointer"
            >
              <option value="all">All Status</option>
              <option value="success">Success</option>
              <option value="failed">Failed</option>
            </select>
          </div>
        </div>
      </FilterCard>

      {selectedIds.length > 0 && (
        <div className="flex items-center justify-between p-4 bg-primary-50 dark:bg-primary-900/20 border border-primary-100 dark:border-primary-800 rounded-xl">
          <span className="text-sm font-medium text-primary-700 dark:text-primary-400">
            {selectedIds.length} record{selectedIds.length !== 1 ? 's' : ''} selected
          </span>
          <button onClick={handleBulkDelete} className="flex items-center gap-2 px-4 py-2.5 bg-red-500 text-white rounded-xl text-sm font-medium hover:bg-red-600 transition-colors">
            <FaTrash className="w-4 h-4" />
            Move to Recycle Bin
          </button>
        </div>
      )}

      {error ? (
        <div className="p-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl text-center">
          <p className="text-red-600 dark:text-red-400 font-medium mb-4">{error}</p>
          <button onClick={fetchHistory} className="btn-premium !py-2.5">Try Again</button>
        </div>
      ) : (
        <DataTable>
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gradient-to-r from-primary-600 to-primary-700 text-white">
                <th className={`${TH} w-8`}>
                  <input
                    type="checkbox"
                    checked={filtered.length > 0 && selectedIds.length === filtered.length}
                    onChange={handleSelectAll}
                    className="rounded w-3.5 h-3.5 cursor-pointer"
                  />
                </th>
                <th className={TH}>{meta.nameLabel}</th>
                {showAdminColumn && <th className={TH}>Admin</th>}
                <th className={TH}>Event</th>
                <th className={TH}>Environment</th>
                <th className={TH}>IP Address</th>
                <th className={TH}>Time</th>
                <th className={TH}>Duration</th>
                <th className={TH}>Status</th>
                <th className={`${TH} text-right`}>Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-dark-700">
              {filtered.length === 0 ? (
                <TableEmpty
                  colSpan={colSpan}
                  icon={TABS.find((t) => t.id === activeTab)?.icon || FaHistory}
                  preset="search"
                  title={meta.emptyTitle}
                  description={meta.emptyDescription}
                />
              ) : filtered.map((item) => {
                const isSelected = selectedIds.includes(item._id);
                const isImpersonation = activeTab === 'impersonation' || item.isImpersonated;
                const eventType = item.eventType || 'login';
                const isLogout = eventType === 'logout';
                return (
                  <tr key={item._id} className={`hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors ${isSelected ? 'bg-primary-50/50 dark:bg-primary-900/20' : ''}`}>
                    <td className={TD}>
                      <input type="checkbox" checked={isSelected} onChange={() => handleSelectOne(item._id)} className="rounded w-3.5 h-3.5 cursor-pointer" />
                    </td>
                    <td className={`${TD_WRAP} max-w-[220px]`}>
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-8 h-8 shrink-0 rounded-lg bg-gradient-to-br from-primary-500 to-primary-600 text-white flex items-center justify-center text-xs font-semibold relative">
                          {item.userName?.[0]?.toUpperCase()}
                          {isImpersonation && (
                            <div className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-purple-500 rounded-full flex items-center justify-center border border-white dark:border-dark-800" title="Impersonated">
                              <FaUserSecret className="w-1.5 h-1.5 text-white" />
                            </div>
                          )}
                        </div>
                        <div className="min-w-0 leading-tight">
                          <p className="text-sm font-semibold text-gray-900 dark:text-white truncate" title={item.userName}>
                            {item.userName}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate" title={item.userEmail}>
                            {item.userEmail}
                          </p>
                        </div>
                      </div>
                    </td>
                    {showAdminColumn && (
                      <td className={TD}>
                        <span className="text-sm text-gray-900 dark:text-white font-medium" title={item.adminLabel}>
                          {item.adminLabel || '—'}
                        </span>
                      </td>
                    )}
                    <td className={TD}>
                      <span className={`badge !px-1.5 !py-0.5 !text-[11px] gap-1 ${isLogout ? 'badge-warning' : 'badge-info'}`}>
                        {isLogout ? <FaSignOutAlt className="w-2.5 h-2.5" /> : <FaSignInAlt className="w-2.5 h-2.5" />}
                        {isLogout ? 'Logout' : 'Login'}
                      </span>
                    </td>
                    <td className={TD}>
                      <span className="inline-flex items-center gap-1.5 text-xs text-gray-700 dark:text-gray-300">
                        {getDeviceIcon(item.device)}
                        <span>{item.os}</span>
                        <span className="text-gray-300 dark:text-gray-600">·</span>
                        <span className="text-gray-500">{item.browser}</span>
                      </span>
                    </td>
                    <td className={TD}>
                      <span className="text-xs font-mono text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-dark-700 px-1.5 py-0.5 rounded">
                        {item.ip || '—'}
                      </span>
                    </td>
                    <td className={`${TD} text-gray-600 dark:text-gray-400 text-xs`}>
                      {formatDate(item.loginTime)}
                    </td>
                    <td className={`${TD} text-gray-600 dark:text-gray-400 text-xs`}>
                      {formatDuration(item.sessionDuration)}
                    </td>
                    <td className={TD}>
                      <span className={`badge !px-1.5 !py-0.5 !text-[11px] gap-1 ${item.status === 'success' ? 'badge-success' : 'badge-error'}`}>
                        {item.status === 'success' ? <FaCheckCircle className="w-2.5 h-2.5" /> : <FaExclamationCircle className="w-2.5 h-2.5" />}
                        {item.status}
                      </span>
                    </td>
                    <td className={`${TD} text-right`}>
                      <button
                        onClick={() => handleDelete(item._id)}
                        className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                        title="Delete"
                      >
                        <FaTrash className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </DataTable>
      )}
    </PageLayout>
  );
};

export default LoginHistory;
